import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import AddIcon from '@material-ui/icons/Add'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import CheckIcon from '@material-ui/icons/Check'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import CloseIcon from '@material-ui/icons/Close'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import EditIcon from '@material-ui/icons/Edit'
import SaveIcon from '@material-ui/icons/Save'
import Autocomplete, {
  createFilterOptions
} from '@material-ui/lab/Autocomplete'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import {
  completeFieldTrip,
  createFieldTrip,
  deleteFieldTrip,
  fetchFieldTrip,
  fetchFieldTrips,
  fetchFieldTripPeople,
  fetchScopedCentres,
  updateFieldTrip
} from 'api'
import {
  ListPagination,
  usePagedRows
} from 'app/components/generic/ListPagination'
import {
  SearchHeadCell,
  useColumnSearch
} from 'app/components/generic/SearchHeadCell'
import constants from 'app/constants'
import { showToast } from 'app/helpers'
import { useOpsCentre } from 'app/hooks/useOpsCentre'

// The list's default centre filter: everything. The API takes this literal
// string ("centre=all"), so it doubles as the Select's value.
const ALL_CENTRES = 'all'

const GRADES = ['Toddlers', 'Pre-Nursery', 'Nursery', 'K1', 'K2']

const NOT_ADDED = 'Not added yet'

// The planning checklist (Shruti, 2026-10-12): the teachers plan the trip
// first, then tick these off from the list as the planning moves along, and
// the line shows how much of the planning is done. The must-do planning steps
// of the Preschool Field Trip SOP. Keys are stored under data.sop and counted
// server-side (PLANNING_KEYS in serializers/field_trip.py) — keep both lists
// the same.
const SOP_CHECKS = [
  ['venue_permission', 'Venue has given permission'],
  ['cd_approval', 'Centre Director has approved'],
  ['parent_consent', 'Parent consent received for every child going'],
  [
    'roles_assigned',
    'Staff roles assigned (supervision, first aid, attendance)'
  ],
  ['volunteers_briefed', 'Parent volunteers briefed'],
  ['kit_ready', 'First aid kit, emergency contacts and attendance sheet ready'],
  ['transport_verified', 'Transport verified']
]

// The form is deliberately small (user, 2026-10-12: "it looks complicated"):
// the trip itself, then the rough plan as five plain sections. Logistics is one
// free-text box — the centre writes transport, cost and permissions in its own
// words rather than filling a field for each.
const blankData = () => ({
  logistics: '',
  teachers: [],
  volunteers: [],
  attendees: [],
  plan: [],
  sop: {}
})

// Parent consent is per child (user, 2026-10-12: "the consent to be per parent,
// right?"). Each attendee carries `consent`; the checklist's parent_consent
// tick is never set by hand — it is true exactly when every child going has a
// yes. Call this on any change to attendees or their consent.
const withConsentTick = (data) => {
  const attendees = Array.isArray(data.attendees) ? data.attendees : []
  return {
    ...data,
    sop: {
      ...(data.sop || {}),
      parent_consent:
        attendees.length > 0 && attendees.every((a) => a.consent === true)
    }
  }
}

// A hand-typed entry may carry its number: "Meera 98860 12345" -> name + phone.
const personFromText = (text) => {
  const raw = String(text || '').trim()
  const match = raw.match(/^(.*?)[\s,:-]*(\+?\d[\d\s]{8,13}\d)$/)
  if (match && match[1].trim()) {
    return { name: match[1].trim(), phone: match[2].replace(/\s+/g, '') }
  }
  return { name: raw, phone: '' }
}

// Teachers and volunteers are {name, phone, ...} rows. Older trips saved them
// as plain names (or {name, role} rows) — read those too.
const people = (list) =>
  (Array.isArray(list) ? list : [])
    .map((each) =>
      typeof each === 'string' ? { name: each, phone: '' } : { ...each }
    )
    .map((each) => ({
      ...each,
      name: String(each.name || '').trim(),
      phone: String(each.phone || '').trim()
    }))
    .filter((each) => each.name)

const samePerson = (a, b) =>
  a.name === b.name && (a.phone || '') === (b.phone || '')

// Read every saved trip through this. The first version of the page stored
// logistics as an object of separate fields and teachers as {name, role} rows;
// fold those into today's simpler shape so nothing written then is lost.
const withDefaults = (data) => {
  const d = data || {}
  let logistics = d.logistics
  if (logistics && typeof logistics === 'object') {
    logistics = [
      logistics.transport && `Transport: ${logistics.transport}`,
      logistics.cost && `Cost: ${logistics.cost}`,
      logistics.consent && `Parent consent: ${logistics.consent}`,
      logistics.venue_permission_note
    ]
      .filter(Boolean)
      .join('\n')
  }
  return {
    ...blankData(),
    ...d,
    logistics: logistics || '',
    teachers: people(d.teachers),
    volunteers: people(d.volunteers),
    // A trip ticked "consent received" before consent was per child had every
    // parent's yes — carry that onto each child rather than lose it.
    attendees: (Array.isArray(d.attendees) ? d.attendees : [])
      .filter((a) => a && a.name)
      .map((a) =>
        a.consent === undefined
          ? { ...a, consent: !!(d.sop && d.sop.parent_consent) }
          : a
      ),
    plan: Array.isArray(d.plan) ? d.plan : [],
    sop: d.sop && typeof d.sop === 'object' ? d.sop : {}
  }
}

const formatDate = (iso) => {
  if (!iso) return ''
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('en-IN', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

// What each list column shows, for the search boxes in its heading.
const TRIP_SEARCH = {
  venue: (t) => t.venue,
  date: (t) => formatDate(t.trip_date),
  grade: (t) => t.grade,
  centre: (t) => t.centre_name,
  status: (t) => t.status
}

const formatDateTime = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  })
}

// Alfred stores a child's class as a short code ("todlr (age 1)", "prenur
// (age 2)", "nur (age 3)", "lkg", "ukg"). Turn it into the grade name the
// centre teams use, so nobody has to decode "prenur".
const squash = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')

const gradeOfStandard = (standard) => {
  const s = squash(standard)
  if (s.startsWith('todlr') || s.startsWith('toddler')) return 'Toddlers'
  if (s.startsWith('prenur')) return 'Pre-Nursery'
  if (s.startsWith('nur')) return 'Nursery'
  if (s.startsWith('lkg') || s.startsWith('k1')) return 'K1'
  if (s.startsWith('ukg') || s.startsWith('k2')) return 'K2'
  return ''
}

const childGrade = (child) =>
  gradeOfStandard(child.standard) || child.standard || 'class not set in Alfred'

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3) },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    gap: theme.spacing(2),
    flexWrap: 'wrap'
  },
  empty: {
    padding: theme.spacing(6),
    textAlign: 'center',
    color: theme.palette.text.secondary
  },
  row: { cursor: 'pointer' },
  // Tighter cells so all seven columns, ending in the Mark complete button,
  // fit a laptop-width window without sideways scrolling.
  listTable: {
    '& th, & td': { padding: theme.spacing(0.75, 1) },
    '& td': { whiteSpace: 'normal' }
  },
  planned: { background: '#fbf0d2', color: '#7a5600', fontWeight: 500 },
  completed: { background: '#e3f3ee', color: '#1e6e58', fontWeight: 500 },
  // Two columns that use the whole page width (they stack on a narrow
  // screen): logistics and staff on the left, children and timings on the right.
  sheet: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    alignItems: 'start',
    gap: theme.spacing(2.5),
    [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr' }
  },
  full: { gridColumn: '1 / -1' },
  section: { padding: theme.spacing(2.5) },
  sectionTitle: { fontWeight: 500, marginBottom: theme.spacing(1.5) },
  fields: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: theme.spacing(2)
  },
  stack: { display: 'flex', flexDirection: 'column', gap: theme.spacing(2.5) },
  line: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    marginBottom: theme.spacing(1)
  },
  muted: { color: theme.palette.text.secondary },
  label: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    marginBottom: theme.spacing(0.5)
  },
  chips: { display: 'flex', flexWrap: 'wrap', gap: theme.spacing(0.75) },
  checks: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: theme.spacing(0, 2)
  },
  preWrap: { whiteSpace: 'pre-wrap' },
  warn: { color: theme.palette.error.main },
  // The trip page: one readable column, the SOP's steps down a rail.
  tripColumn: {
    maxWidth: 780,
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2)
  },
  tripHead: {
    padding: theme.spacing(2.5),
    borderTop: `8px solid ${theme.palette.primary.main}`
  },
  steps: { padding: theme.spacing(2.5) },
  step: {
    display: 'grid',
    gridTemplateColumns: '30px minmax(0, 1fr)',
    gap: theme.spacing(1.5),
    '& + &': { marginTop: theme.spacing(2) }
  },
  dot: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    fontSize: 13,
    fontWeight: 700,
    border: `2px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary
  },
  dotDone: { background: '#1e7a5a', borderColor: '#1e7a5a', color: '#fff' },
  dotNext: {
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main
  },
  stepBody: {
    minWidth: 0,
    paddingBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '$step:last-child &': { borderBottom: 0, paddingBottom: 0 }
  },
  stepHead: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.75)
  },
  stepTitle: { fontWeight: 500, fontSize: 15 },
  stepActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.25),
    '&:empty': { display: 'none' }
  },
  peopleBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    margin: theme.spacing(1, 0),
    maxHeight: 260,
    overflowY: 'auto'
  },
  person: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: theme.spacing(0.5, 2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    padding: theme.spacing(0.75, 1.25)
  },
  phone: { fontVariantNumeric: 'tabular-nums' },
  consentBar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
    margin: theme.spacing(1.25, 0, 1)
  },
  // Tall enough to work in (most of the screen), and it scrolls on its own so
  // the column titles stay frozen at the top of the list.
  consentBox: {
    maxHeight: '65vh',
    overflow: 'auto',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6
  },
  consentRowYes: { background: '#f1f8f5' },
  consentYes: {
    color: '#1e7a5a',
    fontWeight: 500,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4
  },
  timeline: {
    display: 'grid',
    gridTemplateColumns: '70px minmax(0, 1fr)',
    gap: theme.spacing(0.5, 1.5)
  }
}))

const useFormStyles = makeStyles((theme) => ({
  // The form is laid out like the centre's issue form (Shruti, 2026-10-12):
  // one narrow column of cards on a warm page, read top to bottom, with the
  // button at the end — but inside Alfred, not on a separate link.
  page: {
    minHeight: '100%',
    background: '#f3efe8',
    padding: theme.spacing(2, 1.5, 6),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  column: { width: '100%', maxWidth: 660 },
  headerCard: {
    background: '#fff',
    borderRadius: 8,
    border: '1px solid #e3ddd3',
    borderTop: '10px solid #f4511e',
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
    padding: theme.spacing(2.5, 3),
    marginBottom: theme.spacing(1.75)
  },
  desc: { marginTop: theme.spacing(1), color: '#5f5a52', lineHeight: 1.5 },
  card: {
    background: '#fff',
    borderRadius: 8,
    border: '1px solid #e3ddd3',
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(1.75)
  },
  question: {
    fontSize: 15,
    fontWeight: 600,
    color: '#26211b',
    marginBottom: theme.spacing(1.5)
  },
  req: { color: '#d93025', marginLeft: 2 },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2)
  }
}))

// One question on the form: the question, then its answer box.
const Question = ({ title, required, children, f }) => (
  <div className={f.card}>
    <Typography className={f.question}>
      {title}
      {required ? <span className={f.req}>*</span> : null}
    </Typography>
    {children}
  </div>
)

const StatusChip = ({ status, classes }) => (
  <Chip
    size="small"
    label={status === 'completed' ? 'Completed' : 'Planned'}
    className={status === 'completed' ? classes.completed : classes.planned}
  />
)

// Match on everything shown for a person — so a parent is found by the
// child's name, and anyone by their number.
const filterPeople = createFilterOptions({
  stringify: (option) =>
    typeof option === 'string'
      ? option
      : [option.name, option.child, option.role, option.phone]
          .filter(Boolean)
          .join(' ')
})

// A search box over people Alfred already knows (name + phone). Picking one
// brings the number along; a name not in the list can still be typed in.
const PeopleInput = ({
  label,
  options,
  value,
  onChange,
  detail,
  disabled,
  loading,
  placeholder,
  helperText
}) => (
  <Autocomplete
    multiple
    freeSolo
    filterSelectedOptions
    size="small"
    disabled={disabled}
    loading={loading}
    options={options}
    filterOptions={filterPeople}
    value={value}
    getOptionLabel={(option) =>
      typeof option === 'string' ? option : option.name
    }
    getOptionSelected={samePerson}
    renderOption={(option) => (
      <span>
        {option.name}{' '}
        <span style={{ opacity: 0.6 }}>
          {[detail ? detail(option) : '', option.phone || 'no number in Alfred']
            .filter(Boolean)
            .join(' · ')}
        </span>
      </span>
    )}
    renderTags={(tags, getTagProps) =>
      tags.map((tag, index) => (
        <Chip
          key={`${tag.name}-${tag.phone}`}
          size="small"
          label={tag.phone ? `${tag.name} · ${tag.phone}` : tag.name}
          {...getTagProps({ index })}
        />
      ))
    }
    onChange={(e, next) =>
      onChange(
        next
          .map((each) =>
            typeof each === 'string' ? personFromText(each) : each
          )
          .filter((each) => each.name)
      )
    }
    renderInput={(params) => (
      <TextField
        {...params}
        variant="outlined"
        label={label}
        placeholder={placeholder}
        helperText={helperText}
      />
    )}
  />
)

const FieldTrips = () => {
  const classes = useStyles()
  const f = useFormStyles()
  const [centres, setCentres] = useState([])
  const [unrestricted, setUnrestricted] = useState(false)
  // Shared by every Centre Operations page and kept across refreshes.
  const [listCentre, setListCentre, pickListCentre] = useOpsCentre()
  const [statusFilter, setStatusFilter] = useState('')
  const [trips, setTrips] = useState([])
  const [loading, setLoading] = useState(false)

  // 'list' = one line per trip · 'view' = the trip's rough plan, read-only ·
  // 'edit' = the same plan as a form (also used for a new trip, uuid '').
  const [view, setView] = useState('list')
  const [trip, setTrip] = useState(null)
  const [form, setForm] = useState(null)
  const [saving, setSaving] = useState(false)

  // The centre's preschool children (Parent Onboarding board) — what the
  // attendee search box searches.
  const [children, setChildren] = useState([])
  const [staff, setStaff] = useState([])
  const [childrenLoading, setChildrenLoading] = useState(false)

  // null = dialog closed; a string = the after-trip note being typed.
  const [closingNote, setClosingNote] = useState(null)
  // The trip the "mark complete" pop-up is about ({uuid, venue}) — set from a
  // list line or from the trip's own page.
  const [completeTarget, setCompleteTarget] = useState(null)
  // The planning-checklist pop-up: the full trip it is open for + its ticks.
  const [checkTrip, setCheckTrip] = useState(null)
  const [checkTicks, setCheckTicks] = useState({})
  // The consent list on the trip page: a name search and All/Waiting/Received.
  const [consentQuery, setConsentQuery] = useState('')
  const [consentFilter, setConsentFilter] = useState('all')

  useEffect(() => {
    let active = true
    ;(async () => {
      try {
        const data = await fetchScopedCentres()
        if (!active) return
        const list = data?.results || []
        setCentres(list)
        setUnrestricted(Boolean(data?.unrestricted))
        setListCentre((prev) => {
          const allOk = Boolean(data?.unrestricted) || list.length !== 1
          if (
            prev &&
            ((prev === ALL_CENTRES && allOk) ||
              list.some((c) => c.uuid === prev))
          ) {
            return prev
          }
          return allOk ? ALL_CENTRES : list[0].uuid
        })
      } catch (e) {
        showToast('Could not load centres', constants.ERROR)
      }
    })()
    return () => {
      active = false
    }
  }, [])

  const loadTrips = useCallback(async () => {
    if (!listCentre) return
    setLoading(true)
    try {
      const rows = await fetchFieldTrips(listCentre)
      setTrips(Array.isArray(rows) ? rows : [])
    } catch (e) {
      showToast('Could not load field trips', constants.ERROR)
    } finally {
      setLoading(false)
    }
  }, [listCentre])

  useEffect(() => {
    loadTrips()
  }, [loadTrips])

  const formCentre = form?.centre || ''
  useEffect(() => {
    if (view !== 'edit' || !formCentre) {
      setChildren([])
      setStaff([])
      return undefined
    }
    let active = true
    setChildrenLoading(true)
    ;(async () => {
      try {
        const found = await fetchFieldTripPeople(formCentre)
        if (!active) return
        setChildren(Array.isArray(found?.children) ? found.children : [])
        setStaff(Array.isArray(found?.staff) ? found.staff : [])
      } catch (e) {
        if (active) {
          setChildren([])
          setStaff([])
        }
      } finally {
        if (active) setChildrenLoading(false)
      }
    })()
    return () => {
      active = false
    }
  }, [view, formCentre])

  const visibleTrips = useMemo(
    () => trips.filter((t) => !statusFilter || t.status === statusFilter),
    [trips, statusFilter]
  )
  // Heading search boxes and 25 a page, as on Life Catch-Ups (Satya, 29 Oct);
  // a new search or Show filter starts again on page 1.
  const tripSearch = useColumnSearch(visibleTrips, TRIP_SEARCH)
  const { pageRows, paginationProps, resetPage } = usePagedRows(tripSearch.rows)
  useEffect(() => {
    resetPage()
  }, [statusFilter, listCentre, tripSearch.search])

  // The trip's own grade first, then everyone else, each by name — so typing
  // two letters usually lands on the right child at the top.
  const formGrade = form?.grade || ''
  const childOptions = useMemo(
    () =>
      [...children].sort((a, b) => {
        const aFirst = gradeOfStandard(a.standard) === formGrade ? 0 : 1
        const bFirst = gradeOfStandard(b.standard) === formGrade ? 0 : 1
        return aFirst - bFirst || a.name.localeCompare(b.name)
      }),
    [children, formGrade]
  )

  // One line per parent (siblings share a parent), with whose parent they are.
  const parentOptions = useMemo(() => {
    const byKey = {}
    children.forEach((c) => {
      if (!c.parent_name && !c.parent_phone) return
      const name = c.parent_name || `Parent of ${c.name}`
      const key = `${name}|${c.parent_phone}`
      if (byKey[key]) byKey[key].child += `, ${c.name}`
      else byKey[key] = { name, phone: c.parent_phone || '', child: c.name }
    })
    return Object.values(byKey).sort((a, b) => a.name.localeCompare(b.name))
  }, [children])

  const backToList = () => {
    setView('list')
    setTrip(null)
    setForm(null)
    loadTrips()
  }

  const openTrip = async (uuid) => {
    try {
      const full = await fetchFieldTrip(uuid)
      setTrip(full)
      setView('view')
    } catch (e) {
      showToast('Could not open this field trip', constants.ERROR)
    }
  }

  const openNew = () => {
    const only = listCentre !== ALL_CENTRES ? listCentre : ''
    setForm({
      uuid: '',
      centre: only || (centres.length === 1 ? centres[0].uuid : ''),
      venue: '',
      trip_date: '',
      grade: '',
      data: blankData()
    })
    setView('edit')
  }

  const openEdit = () => {
    setForm({
      uuid: trip.uuid,
      centre: trip.centre_uuid,
      venue: trip.venue,
      trip_date: trip.trip_date,
      grade: trip.grade,
      data: withDefaults(trip.data)
    })
    setView('edit')
  }

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }))
  const setData = (key, value) =>
    setForm((f) => ({ ...f, data: { ...f.data, [key]: value } }))
  const setPlanRow = (index, key, value) =>
    setData(
      'plan',
      form.data.plan.map((r, i) => (i === index ? { ...r, [key]: value } : r))
    )

  const save = async () => {
    if (!form.centre) return showToast('Pick the centre', constants.ERROR)
    if (!form.venue.trim()) return showToast('Enter the venue', constants.ERROR)
    if (!form.trip_date) return showToast('Pick the date', constants.ERROR)
    if (!form.grade) return showToast('Pick the grade', constants.ERROR)
    const payload = {
      venue: form.venue.trim(),
      trip_date: form.trip_date,
      grade: form.grade,
      data: withConsentTick({
        ...form.data,
        // Drop lines left empty so the saved plan has no blank rows.
        plan: form.data.plan.filter(
          (r) => (r.time || '').trim() || (r.activity || '').trim()
        )
      })
    }
    setSaving(true)
    try {
      const saved = form.uuid
        ? await updateFieldTrip(form.uuid, payload)
        : await createFieldTrip({ ...payload, centre: form.centre })
      setTrip(saved)
      setForm(null)
      setView('view')
      showToast('Field trip saved', constants.SUCCESS)
    } catch (e) {
      showToast(
        e?.response?.data?.detail || 'Could not save the field trip',
        constants.ERROR
      )
    } finally {
      setSaving(false)
    }
    return undefined
  }

  const askComplete = (target) => {
    setCompleteTarget({ uuid: target.uuid, venue: target.venue })
    setClosingNote('')
  }
  const closeComplete = () => {
    setClosingNote(null)
    setCompleteTarget(null)
  }

  const confirmComplete = async () => {
    if (!completeTarget) return
    setSaving(true)
    try {
      const saved = await completeFieldTrip(completeTarget.uuid, {
        notes: (closingNote || '').trim()
      })
      if (trip && trip.uuid === saved.uuid) setTrip(saved)
      closeComplete()
      loadTrips()
      showToast('Field trip marked complete', constants.SUCCESS)
    } catch (e) {
      showToast(
        e?.response?.data?.detail || 'Could not mark the trip complete',
        constants.ERROR
      )
    } finally {
      setSaving(false)
    }
  }

  const openChecklist = async (uuid) => {
    try {
      const full = await fetchFieldTrip(uuid)
      setCheckTicks(withDefaults(full.data).sop)
      setCheckTrip(full)
    } catch (e) {
      showToast('Could not open the checklist', constants.ERROR)
    }
  }

  const saveChecklist = async () => {
    setSaving(true)
    try {
      const saved = await updateFieldTrip(checkTrip.uuid, {
        data: withConsentTick({
          ...withDefaults(checkTrip.data),
          sop: checkTicks
        })
      })
      if (trip && trip.uuid === saved.uuid) setTrip(saved)
      setCheckTrip(null)
      loadTrips()
      showToast('Planning checklist saved', constants.SUCCESS)
    } catch (e) {
      showToast(
        e?.response?.data?.detail || 'Could not save the checklist',
        constants.ERROR
      )
    } finally {
      setSaving(false)
    }
  }

  // One parent's yes (or all of them), flipped from the consent step.
  const setConsent = async (index, value) => {
    setSaving(true)
    try {
      const current = withDefaults(trip.data)
      const saved = await updateFieldTrip(trip.uuid, {
        data: withConsentTick({
          ...current,
          attendees: current.attendees.map((a, i) =>
            index === 'all' || i === index ? { ...a, consent: value } : a
          )
        })
      })
      setTrip(saved)
    } catch (e) {
      showToast(
        e?.response?.data?.detail || 'Could not save the consent',
        constants.ERROR
      )
    } finally {
      setSaving(false)
    }
  }

  // One tick, flipped straight from its step on the trip page.
  const toggleTick = async (key) => {
    setSaving(true)
    try {
      const current = withDefaults(trip.data).sop
      const saved = await updateFieldTrip(trip.uuid, {
        data: {
          ...(trip.data || {}),
          sop: { ...current, [key]: !current[key] }
        }
      })
      setTrip(saved)
    } catch (e) {
      showToast(
        e?.response?.data?.detail || 'Could not save that step',
        constants.ERROR
      )
    } finally {
      setSaving(false)
    }
  }

  // The two pop-ups, shared by the list and the trip page.
  const ticksDone = SOP_CHECKS.filter(([key]) => checkTicks[key]).length
  const dialogs = (
    <>
      <Dialog
        open={!!checkTrip}
        onClose={() => !saving && setCheckTrip(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Planning checklist{checkTrip ? `: ${checkTrip.venue}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          <Typography className={classes.muted} gutterBottom>
            Tick each step as it gets done. {ticksDone} of {SOP_CHECKS.length}{' '}
            done.
          </Typography>
          <LinearProgress
            variant="determinate"
            value={(ticksDone / SOP_CHECKS.length) * 100}
            style={{ marginBottom: 12, height: 6, borderRadius: 3 }}
          />
          <Box display="flex" flexDirection="column">
            {SOP_CHECKS.map(([key, label]) => (
              <FormControlLabel
                key={key}
                control={
                  <Checkbox
                    color="primary"
                    checked={!!checkTicks[key]}
                    disabled={
                      checkTrip?.status === 'completed' ||
                      key === 'parent_consent'
                    }
                    onChange={(e) =>
                      setCheckTicks({ ...checkTicks, [key]: e.target.checked })
                    }
                  />
                }
                label={
                  key === 'parent_consent'
                    ? `${label} (ticks itself: open the trip and mark each parent's consent)`
                    : label
                }
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={() => setCheckTrip(null)}>
            {checkTrip?.status === 'completed' ? 'Close' : 'Cancel'}
          </Button>
          {checkTrip?.status !== 'completed' && (
            <Button
              variant="contained"
              color="primary"
              disabled={saving}
              onClick={saveChecklist}
            >
              {saving ? 'Saving…' : 'Save checklist'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={closingNote !== null}
        onClose={() => !saving && closeComplete()}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Mark field trip complete
          {completeTarget ? `: ${completeTarget.venue}` : ''}
        </DialogTitle>
        <DialogContent>
          <Typography className={classes.muted} gutterBottom>
            Once marked complete, this trip is locked and can no longer be
            edited or deleted.
          </Typography>
          <TextField
            label="Notes after the trip (optional)"
            variant="outlined"
            size="small"
            fullWidth
            multiline
            rows={3}
            margin="normal"
            placeholder="What worked, what to improve next time"
            value={closingNote || ''}
            onChange={(e) => setClosingNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={closeComplete}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={saving}
            onClick={confirmComplete}
          >
            {saving ? 'Saving…' : 'Confirm, trip is complete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )

  const removeTrip = async () => {
    if (
      !window.confirm(
        `Delete the field trip to "${trip.venue}" on ${formatDate(
          trip.trip_date
        )}? It will be removed from the list.`
      )
    )
      return
    try {
      await deleteFieldTrip(trip.uuid)
      showToast('Field trip deleted', constants.SUCCESS)
      backToList()
    } catch (e) {
      showToast(
        e?.response?.data?.detail || 'Could not delete the field trip',
        constants.ERROR
      )
    }
  }

  // ---------------------------------------------------------------- list
  if (view === 'list') {
    return (
      <Box className={classes.root}>
        <Box className={classes.header}>
          <Typography variant="h5">Field Trips</Typography>
          <Box display="flex" alignItems="center" gridGap={12} flexWrap="wrap">
            <FormControl
              variant="outlined"
              size="small"
              style={{ minWidth: 200 }}
            >
              <InputLabel>Centre</InputLabel>
              <Select
                label="Centre"
                value={listCentre}
                onChange={(e) => pickListCentre(e.target.value)}
              >
                {(unrestricted || centres.length !== 1) && (
                  <MenuItem value={ALL_CENTRES}>All centres</MenuItem>
                )}
                {centres.map((c) => (
                  <MenuItem key={c.uuid} value={c.uuid}>
                    {c.name || c.centre_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              variant="outlined"
              size="small"
              style={{ minWidth: 200 }}
            >
              <InputLabel shrink>Show</InputLabel>
              <Select
                label="Show"
                notched
                displayEmpty
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All trips</MenuItem>
                <MenuItem value="planned">Planned (not done yet)</MenuItem>
                <MenuItem value="completed">Completed (done)</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={openNew}
            >
              New Field Trip
            </Button>
          </Box>
        </Box>
        {/* No sideways-scroll box: the heading sticks to the top of the
            window (a scroll box would stop it), and the cells wrap to fit. */}
        <Box>
          <Table size="small" className={classes.listTable}>
            <TableHead>
              <TableRow>
                <SearchHeadCell
                  label="Venue"
                  search={tripSearch.box('venue')}
                />
                <SearchHeadCell label="Date" search={tripSearch.box('date')} />
                <SearchHeadCell
                  label="Grade"
                  search={tripSearch.box('grade')}
                />
                <SearchHeadCell
                  label="Centre"
                  search={tripSearch.box('centre')}
                />
                <SearchHeadCell label="Children" />
                <SearchHeadCell label="Planning checklist" />
                <SearchHeadCell
                  label="Status"
                  search={tripSearch.box('status')}
                />
                <SearchHeadCell label="" />
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRows.map((t) => (
                <TableRow
                  key={t.uuid}
                  hover
                  className={classes.row}
                  onClick={() => openTrip(t.uuid)}
                >
                  <TableCell
                    style={{
                      fontWeight: 500,
                      whiteSpace: 'normal',
                      minWidth: 140
                    }}
                  >
                    {t.venue}
                  </TableCell>
                  <TableCell>{formatDate(t.trip_date)}</TableCell>
                  <TableCell>{t.grade}</TableCell>
                  <TableCell>{t.centre_name}</TableCell>
                  <TableCell>
                    {t.attendee_count || (
                      <span className={classes.muted}>None yet</span>
                    )}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="flex-start"
                      gridGap={6}
                    >
                      <Box minWidth={110}>
                        <Typography variant="body2">
                          {t.sop_done || 0} of {SOP_CHECKS.length} done
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={((t.sop_done || 0) / SOP_CHECKS.length) * 100}
                          style={{ height: 5, borderRadius: 3 }}
                        />
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => openChecklist(t.uuid)}
                      >
                        {t.status === 'completed' ? 'View' : 'Checklist'}
                      </Button>
                    </Box>
                  </TableCell>
                  {/* One column says where the trip stands AND is the way to
                      move it on: a planned trip shows the button that
                      completes it, a completed one shows when it was done. */}
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {t.status === 'completed' ? (
                      <Box>
                        <StatusChip status={t.status} classes={classes} />
                        {t.completed_at ? (
                          <Typography className={classes.label}>
                            {formatDate(t.completed_at)}
                          </Typography>
                        ) : null}
                      </Box>
                    ) : (
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={<CheckIcon />}
                        onClick={() => askComplete(t)}
                      >
                        Mark complete
                      </Button>
                    )}
                  </TableCell>
                  {/* Clicking anywhere on the row already opens the trip, but
                      nobody found that (Shruti, 2026-10-22) — this button
                      makes it visible and does exactly the same thing. */}
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      endIcon={<ChevronRightIcon />}
                      onClick={() => openTrip(t.uuid)}
                    >
                      Open
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!!visibleTrips.length && !tripSearch.rows.length && (
                <TableRow>
                  <TableCell colSpan={8} className={classes.empty}>
                    No field trips match the search.
                  </TableCell>
                </TableRow>
              )}
              {!visibleTrips.length && (
                <TableRow>
                  <TableCell colSpan={8} className={classes.empty}>
                    {loading
                      ? 'Loading field trips…'
                      : trips.length
                      ? 'No field trips match. Change "Show" to All trips.'
                      : 'No field trips yet. Click "New Field Trip" to plan the first one.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <ListPagination {...paginationProps} />
        </Box>
        {dialogs}
      </Box>
    )
  }

  // ---------------------------------------------------------------- form
  if (view === 'edit' && form) {
    const { data } = form
    const leave = () => (form.uuid ? setView('view') : backToList())
    return (
      <div className={f.page}>
        <div className={f.column}>
          <div className={f.headerCard}>
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              {form.uuid ? 'Edit field trip plan' : 'Plan a field trip'}
            </Typography>
            <Typography className={f.desc}>
              The first four are needed. Fill the rest with what you know now —
              you can come back and add more closer to the date. The planning
              checklist is ticked later, from the Field Trips list.
            </Typography>
          </div>

          <Question f={f} title="Which centre?" required>
            <FormControl
              fullWidth
              variant="outlined"
              size="small"
              disabled={!!form.uuid}
            >
              <Select
                value={form.centre}
                onChange={(e) => {
                  setField('centre', e.target.value)
                  setData('attendees', [])
                }}
              >
                {centres.map((c) => (
                  <MenuItem key={c.uuid} value={c.uuid}>
                    {c.name || c.centre_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Question>
          <Question f={f} title="Which grade is going?" required>
            <FormControl fullWidth variant="outlined" size="small">
              <Select
                value={form.grade}
                onChange={(e) => setField('grade', e.target.value)}
              >
                {GRADES.map((g) => (
                  <MenuItem key={g} value={g}>
                    {g}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Question>
          <Question f={f} title="Where is the trip to? (venue)" required>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              value={form.venue}
              onChange={(e) => setField('venue', e.target.value)}
            />
          </Question>
          <Question f={f} title="When is the trip?" required>
            <TextField
              fullWidth
              type="date"
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={form.trip_date}
              onChange={(e) => setField('trip_date', e.target.value)}
            />
          </Question>
          <Question f={f} title="Logistics">
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              multiline
              rows={3}
              placeholder="Transport, cost, what children wear and carry, venue permission, parent consent"
              value={data.logistics}
              onChange={(e) => setData('logistics', e.target.value)}
            />
          </Question>
          <Question f={f} title="Which teachers are going?">
            <PeopleInput
              options={staff}
              value={data.teachers}
              onChange={(next) => setData('teachers', next)}
              detail={(option) => option.role}
              disabled={!form.centre}
              loading={childrenLoading}
              placeholder={
                form.centre ? 'Search a name and pick' : 'Pick the centre first'
              }
              helperText="From this centre's Team Wall roster, with the phone number. Not in the list? Type the name and number, then press Enter."
            />
          </Question>
          <Question f={f} title="Which parents are volunteering?">
            <PeopleInput
              options={parentOptions}
              value={data.volunteers}
              onChange={(next) => setData('volunteers', next)}
              detail={(option) =>
                option.child ? `parent of ${option.child}` : ''
              }
              disabled={!form.centre}
              loading={childrenLoading}
              placeholder={
                form.centre
                  ? "Search the parent's or child's name"
                  : 'Pick the centre first'
              }
              helperText="Parents of this centre's children, with the number Alfred has. Not in the list? Type the name and number, then press Enter."
            />
          </Question>
          <Question
            f={f}
            title={`Which children are going? (${data.attendees.length} picked)`}
          >
            <Autocomplete
              multiple
              freeSolo
              filterSelectedOptions
              size="small"
              disabled={!form.centre}
              loading={childrenLoading}
              options={childOptions}
              value={data.attendees}
              getOptionLabel={(option) =>
                typeof option === 'string' ? option : option.name
              }
              getOptionSelected={(option, value) =>
                option.uuid
                  ? option.uuid === value.uuid
                  : option.name === value.name
              }
              renderOption={(option) => (
                <span>
                  {option.name}{' '}
                  <span className={classes.muted}>
                    ({childGrade(option)})
                    {option.parent_phone
                      ? ` · parent ${option.parent_phone}`
                      : ''}
                  </span>
                </span>
              )}
              onChange={(e, next) =>
                setData(
                  'attendees',
                  next
                    .map((each) =>
                      typeof each === 'string'
                        ? { uuid: '', name: each.trim(), consent: false }
                        : {
                            uuid: each.uuid || '',
                            name: each.name,
                            parent_name: each.parent_name || '',
                            parent_phone: each.parent_phone || '',
                            consent: each.consent === true
                          }
                    )
                    .filter((each) => each.name)
                )
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder={
                    form.centre
                      ? "Search a child's name and pick"
                      : 'Pick the centre first'
                  }
                  helperText={
                    form.centre
                      ? 'Type a few letters and pick the child. Not in the list? Type the full name and press Enter.'
                      : 'Pick the centre above, then search its children here.'
                  }
                />
              )}
            />
          </Question>
          <Question f={f} title="Time-wise plan for the day">
            {data.plan.map((r, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <Box key={i} className={classes.line}>
                <TextField
                  type="time"
                  variant="outlined"
                  size="small"
                  style={{ width: 140 }}
                  value={r.time || ''}
                  onChange={(e) => setPlanRow(i, 'time', e.target.value)}
                />
                <TextField
                  variant="outlined"
                  size="small"
                  style={{ flex: 1 }}
                  placeholder="What happens, e.g. Leave the centre"
                  value={r.activity || ''}
                  onChange={(e) => setPlanRow(i, 'activity', e.target.value)}
                />
                <IconButton
                  size="small"
                  title="Remove this line"
                  onClick={() =>
                    setData(
                      'plan',
                      data.plan.filter((_, index) => index !== i)
                    )
                  }
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() =>
                setData('plan', [...data.plan, { time: '', activity: '' }])
              }
            >
              Add a time
            </Button>
          </Question>

          <div className={`${f.card} ${f.actions}`}>
            <Button disabled={saving} onClick={leave}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              disabled={saving}
              onClick={save}
            >
              {saving ? 'Saving…' : 'Save field trip'}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // ---------------------------------------------------------------- view
  // The trip's page IS the SOP (user picked "option A", 2026-10-12): its steps
  // run top to bottom, each one holding the part of the plan it is about and
  // the one action that moves it forward. Nothing here is decoration — a step
  // either shows what was planned, says exactly what is missing, or is a
  // button.
  if (!trip) return null
  const data = withDefaults(trip.data)
  const done = trip.status === 'completed'
  const closingText =
    data.closing?.notes ||
    [data.closing?.worked, data.closing?.improve].filter(Boolean).join('\n')
  // Read the consent tick from the children, not from what was last stored —
  // a trip ticked by hand before consent was per child must not show green
  // with nobody picked.
  const { sop } = withConsentTick(data)
  const ticked = SOP_CHECKS.filter(([key]) => sop[key]).length
  const adults = data.teachers.length + data.volunteers.length
  const kids = data.attendees.length
  const consented = data.attendees.filter((a) => a.consent).length
  const consentNeedle = consentQuery.trim().toLowerCase()
  // Keep each child's own index — it is what setConsent saves against.
  const consentRows = data.attendees
    .map((a, i) => ({ a, i }))
    .filter(({ a }) =>
      consentFilter === 'pending'
        ? !a.consent
        : consentFilter === 'received'
        ? !!a.consent
        : true
    )
    .filter(
      ({ a }) =>
        !consentNeedle ||
        `${a.name} ${a.parent_name || ''} ${a.parent_phone || ''}`
          .toLowerCase()
          .includes(consentNeedle)
    )
  const perAdult = trip.grade === 'Toddlers' ? 3 : 5
  const adultsNeeded = Math.ceil(kids / perAdult)
  const peopleDone = !!sop.roles_assigned && !!sop.volunteers_briefed

  // Which step is "next": the first one still open, top to bottom. It gets the
  // filled button so the eye lands on the one thing to do now.
  const order = [
    ['venue', !!sop.venue_permission],
    ['approval', !!sop.cd_approval],
    ['consent', !!sop.parent_consent],
    ['people', peopleDone],
    ['transport', !!sop.transport_verified],
    ['kit', !!sop.kit_ready]
  ]
  const nextStep = done ? '' : (order.find(([, ok]) => !ok) || ['finish'])[0]

  const person = (key, name, detail, phone) => (
    <div key={key} className={classes.person}>
      <span>
        <strong>{name}</strong>
        {detail ? <span className={classes.muted}> · {detail}</span> : null}
      </span>
      <span className={phone ? classes.phone : classes.muted}>
        {phone || 'No number in Alfred'}
      </span>
    </div>
  )

  // A tick's button: "Mark as done" while open, a quiet "Undo" once done.
  const tickButton = (key, stepId, label, undoLabel) =>
    done ? null : sop[key] ? (
      <Button size="small" disabled={saving} onClick={() => toggleTick(key)}>
        {undoLabel || 'Undo'}
      </Button>
    ) : (
      <Button
        size="small"
        color="primary"
        variant={nextStep === stepId ? 'contained' : 'outlined'}
        disabled={saving}
        startIcon={<CheckIcon />}
        onClick={() => toggleTick(key)}
      >
        {label || 'Mark as done'}
      </Button>
    )

  const editLink = (label) =>
    done ? null : (
      <Button size="small" startIcon={<EditIcon />} onClick={openEdit}>
        {label}
      </Button>
    )

  // One SOP step. `state`: done | next | open | info.
  const step = (number, id, title, state, pill, body) => {
    const isNext = nextStep === id && state !== 'done'
    return (
      <div key={id} className={classes.step}>
        <div
          className={`${classes.dot} ${
            state === 'done' ? classes.dotDone : isNext ? classes.dotNext : ''
          }`}
        >
          {state === 'done' ? '✓' : number}
        </div>
        <div className={classes.stepBody}>
          <div className={classes.stepHead}>
            <Typography className={classes.stepTitle}>{title}</Typography>
            <Chip
              size="small"
              label={pill}
              className={
                state === 'done'
                  ? classes.completed
                  : state === 'info'
                  ? ''
                  : classes.planned
              }
            />
          </div>
          {body}
        </div>
      </div>
    )
  }
  const stateOf = (ok) => (ok ? 'done' : 'open')
  const pillOf = (ok) => (ok ? 'Done' : 'Not done yet')

  return (
    <Box className={classes.root}>
      <Box className={classes.tripColumn}>
        <Paper variant="outlined" className={classes.tripHead}>
          <Box className={classes.header} style={{ marginBottom: 8 }}>
            <Box display="flex" alignItems="center" gridGap={8} flexWrap="wrap">
              <IconButton
                size="small"
                title="All field trips"
                onClick={backToList}
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5">{trip.venue}</Typography>
              <StatusChip status={trip.status} classes={classes} />
            </Box>
            {!done && (
              <Box display="flex" alignItems="center" gridGap={8}>
                <Button
                  size="small"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={removeTrip}
                >
                  Delete
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={openEdit}
                >
                  Edit plan
                </Button>
              </Box>
            )}
          </Box>
          <Box className={classes.fields}>
            {[
              ['Date', formatDate(trip.trip_date)],
              ['Grade', trip.grade],
              ['Centre', trip.centre_name],
              ['Children going', kids || 'None picked yet']
            ].map(([label, value]) => (
              <Box key={label}>
                <Typography className={classes.label}>{label}</Typography>
                <Typography style={{ fontWeight: 500 }}>{value}</Typography>
              </Box>
            ))}
          </Box>
          <LinearProgress
            variant="determinate"
            value={(ticked / SOP_CHECKS.length) * 100}
            style={{ height: 6, borderRadius: 3, marginTop: 14 }}
          />
          <Typography className={classes.label} style={{ marginTop: 6 }}>
            {done
              ? `Trip completed${
                  trip.completed_by_name ? ` by ${trip.completed_by_name}` : ''
                }${
                  trip.completed_at
                    ? ` on ${formatDateTime(trip.completed_at)}`
                    : ''
                }. It can no longer be changed.`
              : `Planning: ${ticked} of ${SOP_CHECKS.length} steps done`}
          </Typography>
        </Paper>

        <Paper variant="outlined" className={classes.steps}>
          {step(
            1,
            'plan',
            'Plan the trip',
            'done',
            'Done',
            <Typography className={classes.muted}>
              {trip.venue} · {formatDate(trip.trip_date)} · {trip.grade}
            </Typography>
          )}

          {step(
            2,
            'venue',
            'Venue permission',
            stateOf(sop.venue_permission),
            pillOf(sop.venue_permission),
            <>
              <Typography className={classes.muted}>
                Call the venue 3 to 5 days before. Confirm permission, the entry
                process, allowed areas and washrooms.
              </Typography>
              <Box className={classes.stepActions}>
                {tickButton('venue_permission', 'venue', 'Venue has agreed')}
              </Box>
            </>
          )}

          {step(
            3,
            'approval',
            'Centre Director approval',
            stateOf(sop.cd_approval),
            pillOf(sop.cd_approval),
            <Box className={classes.stepActions}>
              {tickButton('cd_approval', 'approval', 'Director has approved')}
            </Box>
          )}

          {step(
            4,
            'consent',
            'Parent consent',
            stateOf(sop.parent_consent),
            !kids
              ? 'Pick the children first'
              : consented === kids
              ? `All ${kids} received`
              : `${consented} of ${kids} received`,
            <>
              {kids ? (
                <>
                  <Typography className={classes.muted}>
                    Each parent replies yes by email, at least a week before.
                    Tick each child as the yes comes in. No child goes without
                    consent.
                  </Typography>
                  {/* Built for a class of 50: one line per child, a tick
                      instead of a button, a header that stays put while the
                      list scrolls, and ways to cut the list down — find a
                      name, or show only the parents still to reply. */}
                  <Box className={classes.consentBar}>
                    <TextField
                      size="small"
                      variant="outlined"
                      placeholder="Find a child or parent"
                      value={consentQuery}
                      onChange={(e) => setConsentQuery(e.target.value)}
                      style={{ minWidth: 220, flex: 1 }}
                    />
                    {[
                      ['all', `All ${kids}`],
                      ['pending', `Waiting ${kids - consented}`],
                      ['received', `Received ${consented}`]
                    ].map(([key, label]) => (
                      <Chip
                        key={key}
                        size="small"
                        label={label}
                        clickable
                        color={consentFilter === key ? 'primary' : 'default'}
                        onClick={() => setConsentFilter(key)}
                      />
                    ))}
                  </Box>
                  <div className={classes.consentBox}>
                    <Table
                      size="small"
                      stickyHeader
                      className={classes.listTable}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell style={{ width: 90 }}>Consent</TableCell>
                          <TableCell>Child</TableCell>
                          <TableCell>Parent</TableCell>
                          <TableCell>Phone</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {consentRows.map(({ a, i }) => (
                          <TableRow
                            // eslint-disable-next-line react/no-array-index-key
                            key={`${a.uuid || a.name}-${i}`}
                            hover
                            className={a.consent ? classes.consentRowYes : ''}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                color="primary"
                                size="small"
                                checked={!!a.consent}
                                disabled={saving || done}
                                inputProps={{
                                  'aria-label': `Consent received for ${a.name}`
                                }}
                                onChange={(e) =>
                                  setConsent(i, e.target.checked)
                                }
                              />
                            </TableCell>
                            <TableCell style={{ fontWeight: 500 }}>
                              {a.name}
                            </TableCell>
                            <TableCell>
                              {a.parent_name || (
                                <span className={classes.muted}>
                                  Not in Alfred
                                </span>
                              )}
                            </TableCell>
                            <TableCell className={classes.phone}>
                              {a.parent_phone || (
                                <span className={classes.muted}>
                                  No number in Alfred
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                        {!consentRows.length && (
                          <TableRow>
                            <TableCell colSpan={4} className={classes.muted}>
                              {consentQuery
                                ? `Nobody matches "${consentQuery}".`
                                : consentFilter === 'pending'
                                ? 'Nobody is waiting. Every parent has said yes.'
                                : 'No consent has been received yet.'}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </>
              ) : (
                <Typography className={classes.muted}>
                  No children are picked for this trip yet.
                </Typography>
              )}
              <Box className={classes.stepActions}>
                {kids > 1 && consented < kids && !done ? (
                  <Button
                    size="small"
                    disabled={saving}
                    onClick={() => setConsent('all', true)}
                  >
                    All {kids} parents have said yes
                  </Button>
                ) : null}
                {editLink(kids ? 'Change the children' : 'Pick the children')}
              </Box>
            </>
          )}

          {step(
            5,
            'people',
            'Teachers and parent volunteers',
            stateOf(peopleDone),
            peopleDone ? 'Done' : adults ? 'Not done yet' : 'Add the adults',
            <>
              {adults ? (
                <div className={classes.peopleBox}>
                  {data.teachers.map((t, i) =>
                    person(
                      `t-${t.name}-${i}`,
                      t.name,
                      t.role || 'staff',
                      t.phone
                    )
                  )}
                  {data.volunteers.map((v, i) =>
                    person(
                      `v-${v.name}-${i}`,
                      v.name,
                      v.child ? `parent of ${v.child}` : 'parent volunteer',
                      v.phone
                    )
                  )}
                </div>
              ) : (
                <Typography className={classes.muted}>
                  No teachers or parent volunteers are added yet.
                </Typography>
              )}
              {kids > 0 && (
                <Typography
                  className={
                    adults >= adultsNeeded ? classes.muted : classes.warn
                  }
                >
                  {adults} adult{adults === 1 ? '' : 's'} for {kids} child
                  {kids === 1 ? '' : 'ren'}. The SOP needs at least 1 adult per{' '}
                  {perAdult} children
                  {adults >= adultsNeeded
                    ? ', so this is enough.'
                    : `, so add ${adultsNeeded - adults} more.`}
                </Typography>
              )}
              <Box className={classes.stepActions}>
                {adults
                  ? tickButton(
                      'roles_assigned',
                      'people',
                      'Roles assigned (supervision, first aid, attendance)',
                      'Undo: roles assigned'
                    )
                  : null}
                {data.volunteers.length
                  ? tickButton(
                      'volunteers_briefed',
                      'people',
                      'Parent volunteers briefed',
                      'Undo: volunteers briefed'
                    )
                  : adults && !sop.volunteers_briefed
                  ? tickButton(
                      'volunteers_briefed',
                      'people',
                      'No parent volunteers needed'
                    )
                  : sop.volunteers_briefed
                  ? tickButton(
                      'volunteers_briefed',
                      'people',
                      '',
                      'Undo: no volunteers needed'
                    )
                  : null}
                {editLink(adults ? 'Change the adults' : 'Add the adults')}
              </Box>
            </>
          )}

          {step(
            6,
            'transport',
            'Transport and logistics',
            stateOf(sop.transport_verified),
            pillOf(sop.transport_verified),
            <>
              {data.logistics ? (
                <Typography className={classes.preWrap}>
                  {data.logistics}
                </Typography>
              ) : (
                <Typography className={classes.muted}>
                  Nothing written yet: how the children travel, the cost, what
                  they wear and carry.
                </Typography>
              )}
              <Box className={classes.stepActions}>
                {tickButton(
                  'transport_verified',
                  'transport',
                  'Transport confirmed'
                )}
                {editLink(data.logistics ? 'Change' : 'Write the logistics')}
              </Box>
            </>
          )}

          {step(
            7,
            'kit',
            'Day-before kit',
            stateOf(sop.kit_ready),
            pillOf(sop.kit_ready),
            <>
              <Typography className={classes.muted}>
                First aid kit, emergency contact list, attendance sheet and a
                thank-you card for the host.
              </Typography>
              <Box className={classes.stepActions}>
                {tickButton('kit_ready', 'kit', 'Kit is ready')}
              </Box>
            </>
          )}

          {step(
            8,
            'day',
            'On the day',
            'info',
            formatDate(trip.trip_date),
            <>
              {data.plan.length ? (
                <div className={classes.timeline}>
                  {data.plan.map((r, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <React.Fragment key={i}>
                      <strong>{r.time}</strong>
                      <span>{r.activity}</span>
                    </React.Fragment>
                  ))}
                </div>
              ) : (
                <Typography className={classes.muted}>
                  No timings yet. Add when the group leaves, reaches and
                  returns.
                </Typography>
              )}
              <Typography className={classes.muted} style={{ marginTop: 6 }}>
                Headcount before leaving, during the trip and before coming
                back.
              </Typography>
              <Box className={classes.stepActions}>
                {editLink(
                  data.plan.length ? 'Change the timings' : 'Add the timings'
                )}
              </Box>
            </>
          )}

          {step(
            9,
            'finish',
            'After the trip',
            done ? 'done' : 'open',
            done ? 'Completed' : 'When you are back',
            done ? (
              <Typography
                className={closingText ? classes.preWrap : classes.muted}
              >
                {closingText || 'No notes were written.'}
              </Typography>
            ) : (
              <>
                <Typography className={classes.muted}>
                  Recheck attendance on return, hand over the thank-you card,
                  and note what worked and what to improve.
                </Typography>
                <Box className={classes.stepActions}>
                  <Button
                    size="small"
                    color="primary"
                    variant={nextStep === 'finish' ? 'contained' : 'outlined'}
                    startIcon={<CheckIcon />}
                    onClick={() => askComplete(trip)}
                  >
                    Mark field trip complete
                  </Button>
                </Box>
              </>
            )
          )}
        </Paper>
      </Box>
      {dialogs}
    </Box>
  )
}

export default FieldTrips
